// Today-screen banners related to the launch-comp (free month of Plus) period.
//
// Two states:
//  1. Active comp:  user has is_launch_comp === true AND (no expiry OR expiry in future)
//                   AND is not a paying subscriber → subtle persistent banner.
//  2. Expired comp: is_launch_comp === true AND comp_expires_at is in the past
//                   AND is not a paying subscriber → single dismissible banner
//                   nudging them to Plus. PlusGates handle the actual gating.
//
// Paying subscribers and non-comped free users never see either banner.
import { useEffect, useState } from "react";
import { usePlan } from "./ProfileSync";

const DISMISS_KEY = "knowmeno_comp_expired_banner_dismissed_v1";

export function LaunchCompBanner({ onUpgrade }: { onUpgrade?: () => void } = {}) {
  const { isLaunchComp, isPaidPremium, compExpiresAt } = usePlan();
  const [expiredDismissed, setExpiredDismissed] = useState(false);

  useEffect(() => {
    try {
      setExpiredDismissed(window.localStorage.getItem(DISMISS_KEY) === "1");
    } catch {}
  }, []);

  if (!isLaunchComp || isPaidPremium) return null;

  const expired =
    !!compExpiresAt && new Date(compExpiresAt).getTime() < Date.now();

  if (expired) {
    if (expiredDismissed) return null;
    const dismiss = () => {
      try {
        window.localStorage.setItem(DISMISS_KEY, "1");
      } catch {}
      setExpiredDismissed(true);
    };
    return (
      <div
        role="status"
        style={{
          background: "#FDECEF",
          border: "1px solid #F3D7DF",
          borderRadius: 18,
          padding: "14px 16px",
          marginBottom: 14,
          color: "#2D0E43",
          fontSize: 13,
          lineHeight: 1.5,
          display: "flex",
          gap: 12,
          alignItems: "flex-start",
          boxShadow: "0 4px 18px -10px rgba(45,14,67,0.15)",
        }}
      >
        <span aria-hidden style={{ fontSize: 16, lineHeight: 1, marginTop: 1 }}>💜</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, marginBottom: 3, letterSpacing: "-0.005em" }}>
            Your free month has ended
          </div>
          <div style={{ color: "#6E4B73" }}>
            Your tracking and data are still free. Upgrade to Plus to keep
            your full insights.
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          style={{
            background: "transparent",
            border: 0,
            cursor: "pointer",
            color: "#6E4B73",
            fontSize: 18,
            lineHeight: 1,
            padding: 2,
            marginLeft: 4,
          }}
        >
          ×
        </button>
      </div>
    );
  }

  // Active comp banner — two variants based on whether the user has an expiry.
  const hasExpiry = !!compExpiresAt;

  let headline: string;
  let body: string;
  let showCta = false;

  if (hasExpiry) {
    const msRemaining = new Date(compExpiresAt as string).getTime() - Date.now();
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
    if (daysRemaining <= 0) {
      headline = "Your Plus trial ends today";
    } else {
      headline = `Your Plus trial ends in ${daysRemaining} ${daysRemaining === 1 ? "day" : "days"}`;
    }
    body =
      "You're using all of Plus free — including your Doctor Report, trigger detection, and weekly insights. Subscribe to keep them when your trial ends. $9.99/mo or $79/yr.";
    showCta = true;
  } else {
    headline = "Plus is on us — thank you for being an early adopter";
    body = "You have full Plus access as one of our first members.";
  }

  return (
    <div
      role="status"
      style={{
        background: "#FDECEF",
        border: "1px solid #F3D7DF",
        borderRadius: 18,
        padding: "14px 16px",
        marginBottom: 14,
        color: "#2D0E43",
        fontSize: 13,
        lineHeight: 1.5,
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        boxShadow: "0 4px 18px -10px rgba(45,14,67,0.15)",
      }}
    >
      <span aria-hidden style={{ fontSize: 16, lineHeight: 1, marginTop: 1 }}>✨</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, marginBottom: 3, letterSpacing: "-0.005em" }}>{headline}</div>
        <div style={{ color: "#6E4B73" }}>{body}</div>
        {showCta && (
          <button
            type="button"
            onClick={onUpgrade}
            disabled={!onUpgrade}
            style={{
              display: "inline-block",
              marginTop: 12,
              background: "#FF5C7A",
              color: "#FFFFFF",
              border: 0,
              cursor: onUpgrade ? "pointer" : "default",
              fontWeight: 600,
              fontSize: 13,
              padding: "9px 18px",
              borderRadius: 999,
              letterSpacing: "0.01em",
              boxShadow: "0 4px 12px -4px rgba(255,92,122,0.45)",
              transition: "transform 150ms ease, box-shadow 150ms ease",
            }}
          >
            Keep Plus
          </button>
        )}
      </div>
    </div>
  );
}
